// utils/audit.js — shared helpers for the Audit screens (Audit Phase 0).
// Display only: every rule is enforced server-side, never here.

export const CREDENTIAL_TYPES = [
  { value: 'irba_registered_auditor', label: 'IRBA Registered Auditor', needsFirm: true },
  { value: 'accredited_body_member', label: 'Member of an accredited professional body', needsFirm: false },
  { value: 'cc_accounting_officer', label: 'Close Corporation accounting officer', needsFirm: false },
]

export const ROLES = [
  { value: 'responsible_auditor', label: 'Responsible Registered Auditor', raOnly: true },
  { value: 'preparer', label: 'Preparer', raOnly: false },
  { value: 'reviewer', label: 'Reviewer', raOnly: false },
  { value: 'eqr', label: 'Engagement quality reviewer (EQR)', raOnly: true },
]

export const SIGNOFF_LEVELS = [
  { value: 'preparer', label: 'Preparer' },
  { value: 'reviewer', label: 'Reviewer' },
  { value: 'responsible_auditor', label: 'Responsible Registered Auditor' },
  { value: 'eqr', label: 'Engagement quality reviewer' },
]

export const SECTION_STATUSES = [
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
]

export const BASES = [
  { value: 'required_by_law', label: 'Required by law' },
  { value: 'required_by_moi', label: 'Required by the memorandum of incorporation' },
  { value: 'voluntary', label: 'Voluntary' },
]

export const RESULT_LABEL = {
  audit_required: 'Audit required',
  review_required: 'Independent review required',
  neither_required: 'Neither audit nor review required',
  not_applicable: 'Not covered by this check',
}

const lookup = (list, value) => (list.find((x) => x.value === value) || {}).label || value || ''
export const credentialTypeLabel = (v) => lookup(CREDENTIAL_TYPES, v)
export const roleLabel = (v) => lookup(ROLES, v)
export const signoffLabel = (v) => lookup(SIGNOFF_LEVELS, v)
export const basisLabel = (v) => lookup(BASES, v)

export const STATUS_LABEL = {
  attested: 'Self-declared', revoked: 'Ended by KaiLux', withdrawn: 'Withdrawn',
  open: 'Open', report_issued: 'Report issued', file_assembled: 'File assembled',
}

// The server returns {detail:{code,message}} for audit rule refusals,
// a plain string for other 4xx, and a generic sanitised message for 5xx.
export function auditErrorText(err, fallback = 'Something went wrong. Please try again.') {
  const d = err?.response?.data?.detail
  if (d && typeof d === 'object' && !Array.isArray(d) && d.message) return d.message
  if (typeof d === 'string' && d) return d
  if (Array.isArray(d) && d.length) return d.map((x) => x?.msg || String(x)).join('; ')
  return fallback
}

export const auditErrorCode = (err) => {
  const d = err?.response?.data?.detail
  return d && typeof d === 'object' ? d.code || null : null
}

export const formatRand = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? '—'
    : `R${Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`

export function formatDate(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' })
  } catch { return String(iso) }
}

export function formatDateTime(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch { return String(iso) }
}

// Whole days from today until a date (negative when past).
export function daysUntil(iso, now = new Date()) {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  return Math.ceil((t - now.getTime()) / 86400000)
}

// Latest, non-superseded sign-off per level.
export function currentSignoffs(signoffs = []) {
  const out = {}
  signoffs.filter((s) => !s.superseded_at).forEach((s) => { out[s.level] = s })
  return out
}

// section_key -> row
export const sectionMap = (sections = []) => Object.fromEntries(sections.map((s) => [s.section_key, s]))

export function sectionProgress(required = [], sections = []) {
  const m = sectionMap(sections)
  const done = required.filter((r) => m[r.section_key]?.status === 'completed').length
  return { done, total: required.length }
}

// Which credential (if any) lets this person act as a Registered Auditor right now.
export function validRaCredential(credentials = [], firmUserId, now = new Date()) {
  return credentials.find((c) =>
    c.firm_user_id === firmUserId && c.credential_type === 'irba_registered_auditor' &&
    c.status === 'attested' && c.next_review_at && new Date(c.next_review_at) > now) || null
}

export const isLocked = (eng) => !!eng && eng.status !== 'open'

const DAY = 86400000
// Assembly deadline status for a report-issued engagement.
export function assemblyStatus(eng, now = new Date()) {
  if (!eng || eng.status === 'open') return null
  if (eng.status === 'file_assembled') return { state: eng.assembled_late ? 'late' : 'done', days: null }
  const days = Math.ceil((new Date(eng.assembly_due_at).getTime() - now.getTime()) / DAY)
  return { state: days < 0 ? 'overdue' : days <= 14 ? 'due_soon' : 'ok', days }
}
